/* ============================================================
   Wishlist Manager — works for guests (localStorage)
   and logged-in users (localStorage + metafield sync)
   ============================================================ */

(function () {
  'use strict';

  const STORAGE_KEY  = 'zest_wishlist_v1';
  const META_NS      = 'custom';
  const META_KEY     = 'wishlist';
  const TOAST_DURATION = 2800;

  /* ── Helpers ─────────────────────────────────────── */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
  const emit = (name, detail) =>
    document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));

  /* ── Storage (localStorage) ──────────────────────── */
  function storageGet() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
    catch { return []; }
  }

  function storageSet(list) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  }

  /* ── Metafield sync for logged-in users ──────────── */
  // Reads the server-side wishlist rendered in Liquid and merges on page load.
  // Writes happen via a hidden form POST (no private app needed).
  function mergeServerWishlist(serverIds) {
    if (!serverIds || !serverIds.length) return;
    const local = storageGet();
    const localIds = local.map(i => String(i.id));
    serverIds.forEach(id => {
      if (!localIds.includes(String(id))) {
        local.push({ id: String(id), synced: true });
      }
    });
    storageSet(local);
  }

  /* ── Core API ─────────────────────────────────────── */
  const Wishlist = {
    _list: [],

    init() {
      this._list = storageGet();

      // Merge server wishlist for logged-in users
      const serverData = window.__wishlistServerIds || [];
      if (serverData.length) mergeServerWishlist(serverData);
      this._list = storageGet(); // re-read after merge

      this._bindEvents();
      this._updateAllUI();

      // Expose globally
      window.Wishlist = this;
    },

    has(productId) {
      return this._list.some(i => String(i.id) === String(productId));
    },

    add(productId, meta = {}) {
      if (this.has(productId)) return;
      this._list.push({
        id:     String(productId),
        title:  meta.title  || '',
        handle: meta.handle || '',
        image:  meta.image  || '',
        price:  meta.price  || '',
        url:    meta.url    || `/products/${meta.handle}`,
        vendor: meta.vendor || '',
        addedAt: Date.now(),
      });
      storageSet(this._list);
      this._persist();
      this._updateAllUI();
      emit('wishlist:added', { productId, meta });
    },

    remove(productId) {
      this._list = this._list.filter(i => String(i.id) !== String(productId));
      storageSet(this._list);
      this._persist();
      this._updateAllUI();
      emit('wishlist:removed', { productId });
    },

    toggle(productId, meta = {}) {
      if (this.has(productId)) { this.remove(productId); return false; }
      else { this.add(productId, meta); return true; }
    },

    getAll() { return [...this._list]; },

    count() { return this._list.length; },

    /* Save wishlist IDs to customer metafield via hidden form */
    _persist() {
      if (!window.__wishlistCustomerId) return; // guest — skip
      const ids = this._list.map(i => i.id).join(',');
      fetch('/account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          _method: 'patch',
          [`customer[metafields][0][namespace]`]: META_NS,
          [`customer[metafields][0][key]`]:       META_KEY,
          [`customer[metafields][0][value]`]:      ids,
          [`customer[metafields][0][type]`]:       'single_line_text_field',
        }),
      }).catch(() => {}); // silent fail — localStorage is the source of truth
    },

    /* ── UI Updates ─────────────────────────────────── */
    _updateAllUI() {
      const count = this.count();

      // Header counter badges
      $$('[data-wishlist-count]').forEach(el => {
        el.textContent = count;
        el.hidden = count === 0;
      });

      // All wishlist buttons on the page
      $$('[data-wishlist-btn]').forEach(btn => {
        const id = String(btn.dataset.productId);
        const active = this.has(id);
        btn.classList.toggle('is-active', active);
        btn.setAttribute(
          'aria-label',
          active
            ? (btn.dataset.removeLabel || 'Remove from wishlist')
            : (btn.dataset.addLabel   || 'Add to wishlist')
        );
        btn.setAttribute('aria-pressed', active);

        // Swap SVG fill for active state
        const svg = btn.querySelector('svg');
        if (svg) svg.classList.toggle('filled', active);
      });

      emit('wishlist:ui-updated', { count });
    },

    /* ── Event delegation ───────────────────────────── */
    _bindEvents() {
      document.addEventListener('click', e => {
        const btn = e.target.closest('[data-wishlist-btn]');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();

        const id  = btn.dataset.productId;
        const meta = {
          title:  btn.dataset.productTitle  || '',
          handle: btn.dataset.productHandle || '',
          image:  btn.dataset.productImage  || '',
          price:  btn.dataset.productPrice  || '',
          url:    btn.dataset.productUrl    || '',
          vendor: btn.dataset.productVendor || '',
        };

        btn.classList.add('is-animating');
        setTimeout(() => btn.classList.remove('is-animating'), 400);

        const added = this.toggle(id, meta);
        Toast.show(added
          ? (btn.dataset.toastAdded   || 'Added to wishlist ♡')
          : (btn.dataset.toastRemoved || 'Removed from wishlist'));
      });

      // Re-init buttons injected by AJAX (product quick-view, etc.)
      document.addEventListener('wishlist:reinit', () => this._updateAllUI());
    },
  };

  /* ── Toast Notification ──────────────────────────── */
  const Toast = {
    _el: null,
    _timer: null,

    _build() {
      if (this._el) return;
      this._el = document.createElement('div');
      this._el.id        = 'wishlist-toast';
      this._el.className = 'wishlist-toast';
      this._el.setAttribute('role', 'status');
      this._el.setAttribute('aria-live', 'polite');
      document.body.appendChild(this._el);
    },

    show(msg) {
      this._build();
      this._el.textContent = msg;
      this._el.classList.add('is-visible');
      clearTimeout(this._timer);
      this._timer = setTimeout(
        () => this._el.classList.remove('is-visible'),
        TOAST_DURATION
      );
    },
  };

  /* ── Wishlist Page renderer ──────────────────────── */
  const WishlistPage = {
    init(container) {
      if (!container) return;
      this._container = container;
      this._render();

      document.addEventListener('wishlist:removed', () => this._render());
      document.addEventListener('wishlist:added',   () => this._render());
    },

    async _render() {
      const items = Wishlist.getAll();
      const grid  = this._container.querySelector('[data-wishlist-grid]');
      const empty = this._container.querySelector('[data-wishlist-empty]');
      if (!grid) return;

      if (!items.length) {
        grid.innerHTML  = '';
        if (empty) empty.hidden = false;
        return;
      }

      if (empty) empty.hidden = true;
      grid.innerHTML = '<div class="wishlist-page__loading">Loading your wishlist…</div>';

      // Fetch live product data for all items in parallel
      const products = await Promise.all(
        items.map(item =>
          fetch(`/products/${item.handle}.js`)
            .then(r => r.ok ? r.json() : null)
            .catch(() => null)
        )
      );

      grid.innerHTML = '';
      products.forEach((product, idx) => {
        if (!product) return;
        const item = items[idx];
        grid.appendChild(this._buildCard(product, item));
      });

      // Re-init wishlist buttons inside newly rendered cards
      Wishlist._updateAllUI();
    },

    // _buildCard(product, item) {
    //   const variant  = product.variants[0];
    //   const imgSrc   = product.featured_image || item.image || '';
    //   const price    = this._formatMoney(variant.price);
    //   const origPrice = variant.compare_at_price
    //     ? this._formatMoney(variant.compare_at_price)
    //     : null;

    //   const div = document.createElement('div');
    //   div.className = 'wishlist-card';
    //   div.innerHTML = `
    //     <a href="/products/${product.handle}" class="wishlist-card__img-wrap">
    //       <img src="${imgSrc}" alt="${product.title}" loading="lazy" class="wishlist-card__img" />
    //     </a>
    //     <div class="wishlist-card__body">
    //       <p class="wishlist-card__vendor">${product.vendor}</p>
    //       <a href="/products/${product.handle}" class="wishlist-card__title">${product.title}</a>
    //       <div class="wishlist-card__price">
    //         <span class="wishlist-card__price--current">${price}</span>
    //         ${origPrice ? `<s class="wishlist-card__price--compare">${origPrice}</s>` : ''}
    //       </div>
    //       <div class="wishlist-card__actions">
    //         <product-form class="f-product-form f-main-product-form">
    //             {%- form 'product',
    //               product,
    //               id: product_form_id,
    //               class: 'main-product-form',
    //               novalidate: 'novalidate',
    //               data-type: 'add-to-cart-form'
    //             -%}
    //               <input type="hidden" name="id" value="{{ product.selected_or_first_available_variant.id }}" disabled>
    //               {%- if gift_card_recipient_feature_active -%}
    //                 {%- render 'gift-card-recipient-form', product: product, form: form, section: section -%}
    //               {%- endif -%}
    //               <div class="f-product-form__buttons{% if show_dynamic_checkout and product.selling_plan_groups == empty %} f-product-form__buttons--show-buynow{% endif %}">
    //                 {% if block.settings.show_atc_button %}
    //                   {%- liquid
    //                     assign check_against_inventory = true
    //                     if product.selected_or_first_available_variant.inventory_management != 'shopify' or product.selected_or_first_available_variant.inventory_policy == 'continue'
    //                       assign check_against_inventory = false
    //                     endif
    //                     if product.selected_or_first_available_variant.quantity_rule.min > product.selected_or_first_available_variant.inventory_quantity and check_against_inventory
    //                       assign quantity_rule_soldout = true
    //                     endif
    //                   -%}
    //                   <button
    //                     id="ProductSubmitButton-{{ section.id }}"
    //                     type="submit"
    //                     name="add"
    //                     class="f-product-form__submit btn {% if show_dynamic_checkout and product.selling_plan_groups == empty %}btn--secondary{% else %}btn--primary{% endif %}"
    //                     {% if product.selected_or_first_available_variant.available == false
    //                       or quantity_rule_soldout
    //                       or product.selected_or_first_available_variant == null
    //                     %}
    //                       disabled
    //                     {% endif %}
    //                   >
    //                     <span>
    //                       {%- if product.selected_or_first_available_variant.available -%}
    //                         {{ 'products.product.add_to_cart' | t }}
    //                       {%- else -%}
    //                         {{ 'products.product.sold_out' | t }}
    //                       {%- endif -%}
    //                     </span>
    //                     {% comment %} theme-check-disable {% endcomment %}
    //                     {{ 'icon-spinner.svg' | inline_asset_content }}
    //                     {% comment %} theme-check-enable {% endcomment %}
    //                   </button>
    //                 {% endif %}
    //                 {%- if show_dynamic_checkout -%}
    //                   <div class="f-product-form__button-dynamic">
    //                     {{ form | payment_button }}
    //                   </div>
    //                 {%- endif -%}
    //                 <div class="f-product-form__error-message-wrapper" role="alert"></div>
    //               </div>
    //             {%- endform -%}
    //           </product-form>
    //         // <button
    //         //   class="wishlist-btn wishlist-card__remove"
    //         //   data-wishlist-btn
    //         //   data-product-id="${product.id}"
    //         //   data-product-handle="${product.handle}"
    //         //   data-toast-removed="Removed from wishlist"
    //         //   aria-label="Remove from wishlist"
    //         //   title="Remove from wishlist"
    //         // >${this._heartSVG()}</button>
    //         <button
    //           class="wishlist-btn wishlist-card__remove"
    //           data-wishlist-btn
    //           data-product-id="${product.id}"
    //           data-product-handle="${product.handle}"
    //           data-toast-removed="Removed from wishlist"
    //           aria-label="Remove from wishlist"
    //           title="Remove from wishlist"
    //         >X</button>
    //       </div>
    //     </div>`;

    //   // Add to Cart from wishlist page
    //   div.querySelector('[data-atc-btn]').addEventListener('click', async (e) => {
    //     const btn = e.currentTarget;
    //     btn.textContent = 'Adding…';
    //     btn.disabled = true;
    //     try {
    //       await fetch('/cart/add.js', {
    //         method: 'POST',
    //         headers: { 'Content-Type': 'application/json' },
    //         body: JSON.stringify({ id: btn.dataset.variantId, quantity: 1 }),
    //       });
    //       btn.textContent = 'Added ✓';
    //       // Trigger Zest cart drawer refresh
    //       document.dispatchEvent(new CustomEvent('cart:refresh'));
    //     } catch {
    //       btn.textContent = 'Error — retry';
    //       btn.disabled = false;
    //     }
    //   });

    //   return div;
    // },
    // _buildCard(product, item) {
    //   const variant  = product.variants[0];
    //   const imgSrc   = product.featured_image || item.image || '';
    //   const price    = this._formatMoney(variant.price);
    //   const origPrice = variant.compare_at_price
    //     ? this._formatMoney(variant.compare_at_price)
    //     : null;

    //   const div = document.createElement('div');
    //   div.className = 'wishlist-card';
    //   div.innerHTML = `
    //     <a href="/products/${product.handle}" class="wishlist-card__img-wrap">
    //       <img src="${imgSrc}" alt="${product.title}" loading="lazy" class="wishlist-card__img" />
    //     </a>
    //     <div class="wishlist-card__body">
    //       <p class="wishlist-card__vendor">${product.vendor}</p>
    //       <a href="/products/${product.handle}" class="wishlist-card__title">${product.title}</a>
    //       <div class="wishlist-card__price">
    //         <span class="wishlist-card__price--current">${price}</span>
    //         ${origPrice ? `<s class="wishlist-card__price--compare">${origPrice}</s>` : ''}
    //       </div>
    //       <div class="wishlist-card__actions">

    //         <product-form class="f-product-form f-main-product-form">
    //           <form
    //             method="post"
    //             action="/cart/add"
    //             class="main-product-form"
    //             novalidate="novalidate"
    //             data-type="add-to-cart-form"
    //           >
    //             <input type="hidden" name="id" value="${variant.id}">
    //             <input type="hidden" name="quantity" value="1">
    //             <div class="f-product-form__buttons f-product-form__buttons--show-buynow">
    //               <button
    //                 type="submit"
    //                 name="add"
    //                 class="f-product-form__submit btn btn--primary"
    //                 ${variant.available ? '' : 'disabled'}
    //               >
    //                 <span>${variant.available ? 'Add to Cart' : 'Sold Out'}</span>
    //               </button>
    //             </div>
    //           </form>
    //         </product-form>

    //         <button
    //           class="wishlist-btn wishlist-card__remove"
    //           data-wishlist-btn
    //           data-product-id="${product.id}"
    //           data-product-handle="${product.handle}"
    //           data-toast-removed="Removed from wishlist"
    //           aria-label="Remove from wishlist"
    //           type="button"
    //         >${this._heartSVG()}</button>

    //       </div>
    //     </div>`;

    //   return div;
    // },
    _buildCard(product, item) {
  const variant = product.variants[0];
  const imgSrc = product.featured_image || item.image || '';
  const secondimg = product.images && product.images.length > 1 ? product.images[1] : imgSrc;
  const price = this._formatMoney(variant.price);
  const origPrice = variant.compare_at_price ? this._formatMoney(variant.compare_at_price) : null;
  const div = document.createElement('div');
  // div.className = 'product-card grid-list__column quick-add-enabled';
  // div.setAttribute('swatch-generated', `${product.handle}`);
  
  div.innerHTML = `
    <div class="product-card__wrapper">
      <!-- Main Link -->
      <a href="/products/${product.handle}" class="full-width-link product-card__link">
        <span class="visually-hidden">${product.title}</span>
      </a>
      
      <!-- Image Wrapper -->
      <div class="product-card__image-wrapper f-card">
        <!-- Main Image -->
        <responsive-image class="f-image product-card__image product-card__image--main" style="--aspect-ratio: 0.7081545064377682" intersecting="true">
          <img src="${imgSrc}" 
               alt="${product.title}" 
               class="motion-reduce f-img-loaded" 
               loading="lazy" 
               width="473" 
               height="668">
        </responsive-image>
        <responsive-image class="f-image f-image--no-zoom product-card__image product-card__image--second inset-full hidden md:block" style="--aspect-ratio: 0.7081545064377682" intersecting="true">
          <img src="${secondimg}" 
               alt="${product.title}" 
               class="motion-reduce f-img-loaded" 
               loading="lazy" 
               width="473" 
               height="668">
        </responsive-image>
        

        <!-- Wishlist Remove Button (Heart Icon) -->
        <button class="wishlist-btn wishlist-btn--card" 
                data-wishlist-btn 
                data-product-id="${product.id}"
                data-product-handle="${product.handle}"
                data-product-title="${product.title}"
                data-product-image="${imgSrc}"
                data-product-price="${variant.price}"
                data-product-url="/products/${product.handle}"
                data-product-vendor="${product.vendor}"
                data-add-label="Add to wishlist"
                data-remove-label="Remove from wishlist"
                data-toast-added="${product.title} added to wishlist"
                data-toast-removed="Removed from wishlist"
                aria-label="Remove from wishlist"
                aria-pressed="false" 
                type="button">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>

      <!-- Product Info with Add to Cart after Price -->
      <div class="product-card__info text-left" style="padding-left: 15px;">
        <div class="prodcut-card_wrapper">
          <h3 class="product-card__title text-product-title font-body">${product.title}</h3>
        </div>
        
        <!-- Price -->
        <div class="f-price f-price--left">
          <div class="f-price__regular">
            <span class="visually-hidden visually-hidden--inline">Regular price</span>
            <span class="f-price-item f-price-item--regular">${price}</span>
          </div>
          ${origPrice ? `
          <div class="f-price__sale">
            <span class="visually-hidden visually-hidden--inline">Sale price</span>
            <span class="f-price-item f-price-item--sale">${price}</span>
            <span class="visually-hidden visually-hidden--inline">Regular price</span>
            <s class="f-price-item f-price-item--regular">${origPrice}</s>
          </div>
          ` : ''}
        </div>
        </div>
        <!-- Add to Cart Form - Positioned after price, no hover -->
        <product-form class="f-product-form f-main-product-form">
          <form method="post" 
                action="/cart/add" 
                class="main-product-form" 
                novalidate="novalidate" 
                data-type="add-to-cart-form">
            <input type="hidden" name="id" value="${variant.id}">
            <input type="hidden" name="quantity" value="1">
            <div class="f-product-form__buttons f-product-form__buttons--show-buynow">
              <button id="ProductSubmitButton-wishlist-${product.id}" 
                      type="submit" 
                      name="add" 
                      class="f-product-form__submit btn btn--primary size-selected ${!variant.available ? 'disabled' : ''}">
                <span>${variant.available ? 'Add to cart' : 'Sold Out'}</span>
                <svg aria-hidden="true" focusable="false" role="presentation" class="f-icon-svg f-icon--medium f-spinner-icon" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                  <circle fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                </svg>
              </div>
          </form>
        </product-form>
      
    </div>
  `;

  return div;
},
    _formatMoney(cents) {
      const sym = window.Shopify?.currency?.active || 'USD';
      return (cents / 100).toLocaleString('en-IN', {
        style: 'currency', currency: sym, maximumFractionDigits: 2,
      });
    },

    _heartSVG() {
      return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
      </svg>`;
    },
  };

  /* ── Boot ─────────────────────────────────────────── */
  function boot() {
    Wishlist.init();
    WishlistPage.init(document.querySelector('[data-wishlist-page]'));
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', boot)
    : boot();

})();